// src/pages/BookingPage.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Divider, Chip, Button, TextField,
  Tabs, Tab, Stack, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Alert, CircularProgress, Tooltip, Paper,
  Avatar, Grid, InputAdornment, Fade, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, FormControl, InputLabel, OutlinedInput, useMediaQuery,
  Badge, Zoom, Grow, Slide
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { tr, enUS, de } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { useNotify } from "react-admin";
import { alpha, styled } from '@mui/material/styles';

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import NotesIcon from "@mui/icons-material/Notes";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LanguageIcon from "@mui/icons-material/Language";
import WorkIcon from "@mui/icons-material/Work";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import FreeBreakfastIcon from "@mui/icons-material/FreeBreakfast";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EventBusyIcon from "@mui/icons-material/EventBusy";

const API = "https://api.haymestasarim.com/api";

/* ==================== STYLED COMPONENTS ==================== */
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
  },
}));

const GradientHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, #1976d2 0%, #f57c00 100%)`,
  color: 'white',
  borderRadius: 16,
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: 8,
  backgroundColor: statuscolor === 'warning' ? '#fff3e0' : 
                  statuscolor === 'success' ? '#e8f5e8' :
                  statuscolor === 'error' ? '#ffebee' : '#e3f2fd',
  color: statuscolor === 'warning' ? '#ed6c02' :
         statuscolor === 'success' ? '#2e7d32' :
         statuscolor === 'error' ? '#d32f2f' : '#0288d1',
  border: 'none',
}));

/* ==================== RENK PALETİ ==================== */
const COLORS = {
  primary: "#1976d2",
  secondary: "#f57c00",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  purple: "#9c27b0",
  teal: "#008080",
};

/* ==================== ENDPOINT'LER ==================== */
const ENDPOINTS = {
  bookings: API + "/randevu/bookings",
  approve: (id) => API + "/randevu/bookings/" + id + "/approve",
  reject: (id) => API + "/randevu/bookings/" + id + "/reject",
  propose: (id) => API + "/randevu/bookings/" + id + "/propose",
  note: (id) => API + "/randevu/bookings/" + id + "/note",
  settingsGet: API + "/randevu/settings",
  settingsSave: API + "/randevu/settings",
};

/* ==================== YARDIMCI FONKSİYONLAR ==================== */
function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    Authorization: "Bearer " + token,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

function getUiLang() {
  const v = localStorage.getItem("app_lang") || localStorage.getItem("lang") || "tr";
  return ["en", "de", "tr"].includes(v) ? v : "tr";
}

const getDateFnsLocale = (lang) => {
  switch (lang) {
    case "tr": return tr;
    case "de": return de;
    default: return enUS;
  }
};

/* ==================== SABİTLER ==================== */
const DAYS = [
  { key: "monday", tr: "Pazartesi", en: "Monday", de: "Montag", short: "Pzt" },
  { key: "tuesday", tr: "Salı", en: "Tuesday", de: "Dienstag", short: "Sal" },
  { key: "wednesday", tr: "Çarşamba", en: "Wednesday", de: "Mittwoch", short: "Çar" },
  { key: "thursday", tr: "Perşembe", en: "Thursday", de: "Donnerstag", short: "Per" },
  { key: "friday", tr: "Cuma", en: "Friday", de: "Freitag", short: "Cum" },
  { key: "saturday", tr: "Cumartesi", en: "Saturday", de: "Samstag", short: "Cmt" },
  { key: "sunday", tr: "Pazar", en: "Sunday", de: "Sonntag", short: "Paz" },
];

const SLOT_DURATIONS = [15, 20, 30, 45, 60, 90, 120];

/* ==================== I18N ==================== */
const I18N = {
  tr: {
    // Genel
    pageTitle: "Randevu Yönetim Paneli",
    pageDesc: "Tüm randevularınızı tek merkezden yönetin, onaylayın, reddedin.",
    refresh: "Yenile",
    loading: "Yükleniyor...",
    empty: "Henüz randevu bulunmuyor.",
    save: "Kaydet",
    saving: "Kaydediliyor...",
    cancel: "İptal",
    delete: "Sil",
    edit: "Düzenle",
    add: "Ekle",
    
    // İstatistikler
    statsTotal: "Toplam",
    statsPending: "Onay Bekleyen",
    statsApproved: "Onaylanan",
    statsRejected: "Reddedilen",
    statsToday: "Bugünkü",
    
    // Filtreler
    searchPlaceholder: "Ara: isim, telefon, tarih...",
    statusLabel: "Durum",
    statusAll: "Tümü",
    statusPending: "Bekleyen",
    statusApproved: "Onaylanan",
    statusRejected: "Reddedilen",
    statusProposed: "Önerilen",
    startDate: "Başlangıç",
    endDate: "Bitiş",
    clearFilters: "Temizle",
    
    // Durum Chip'leri
    chipPending: "Bekliyor",
    chipApproved: "Onaylandı",
    chipRejected: "Reddedildi",
    chipProposed: "Önerildi",
    
    // Butonlar
    btnApprove: "Onayla",
    btnReject: "Reddet",
    btnPropose: "Saat Öner",
    btnNote: "Not Ekle",
    btnSettings: "Ayarlar",
    
    // Bildirimler
    notifyFetchFail: "Randevular alınamadı",
    notifyApproveOk: "Randevu onaylandı",
    notifyRejectOk: "Randevu reddedildi",
    proposeOk: "Saat önerisi gönderildi",
    proposeFail: "Saat önerilemedi",
    noteSaved: "Not kaydedildi",
    noteSaveFail: "Not kaydedilemedi",
    settingsSaved: "Ayarlar kaydedildi",
    settingsSaveFail: "Ayarlar kaydedilemedi",
    
    // Ayarlar
    settingsTitle: "Randevu Ayarları",
    settingsDesc: "Çalışma saatlerinizi ve özel günlerinizi belirleyin.",
    generalSettings: "Genel Ayarlar",
    workingHours: "Çalışma Saatleri",
    annualCalendar: "Yıllık Takvim",
    slotDuration: "Randevu Süresi",
    gapMinutes: "Arası Boşluk",
    weeklyHours: "Haftalık Çalışma Saatleri",
    lunchBreak: "Öğle Arası",
    open: "Açık",
    closed: "Kapalı",
    start: "Başlangıç",
    end: "Bitiş",
    minutes: "dk",
    addSpecialDay: "Özel Gün Ekle",
    date: "Tarih",
    description: "Açıklama",
    exceptionType: "Tip",
    exceptionTypes: {
      working: "Çalışma Günü (Tatilde Çalış)",
      closed: "Kapalı Gün (Tatil)",
    },
    
    // Günlük Randevu Ekleme
    dailyBookingTitle: "Günlük Randevu Ekle",
    dailyBookingDesc: "Belirli bir güne özel randevu ekleyin.",
    selectDate: "Tarih Seç",
    addTimeSlot: "Saat Aralığı Ekle",
    customerName: "Müşteri Adı",
    customerPhone: "Telefon",
    customerEmail: "E-posta",
    note: "Not",
    saveDailyBooking: "Randevuyu Kaydet",
    
    // Propose Dialog
    proposeTitle: "Yeni Randevu Saati Öner",
    proposeInfo: "Müşteriye yeni bir randevu saati önereceksiniz.",
    proposeDate: "Tarih",
    proposeStart: "Başlangıç",
    proposeEnd: "Bitiş",
    proposeMessage: "Mesaj",
    
    // Note Dialog
    noteTitle: "Kurum Notu",
    notePlaceholder: "Kurum içi notunuzu yazın...",
    
    // Uyarılar
    warnDisabled: "Randevu sistemi kapalı. Ayarlar'dan aktifleştirin.",
  },
  en: {
    pageTitle: "Appointment Management",
    pageDesc: "Manage all your appointments in one place.",
    refresh: "Refresh",
    loading: "Loading...",
    empty: "No appointments found.",
    save: "Save",
    saving: "Saving...",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    
    statsTotal: "Total",
    statsPending: "Pending",
    statsApproved: "Approved",
    statsRejected: "Rejected",
    statsToday: "Today",
    
    searchPlaceholder: "Search: name, phone, date...",
    statusLabel: "Status",
    statusAll: "All",
    statusPending: "Pending",
    statusApproved: "Approved",
    statusRejected: "Rejected",
    statusProposed: "Proposed",
    startDate: "Start",
    endDate: "End",
    clearFilters: "Clear",
    
    chipPending: "Pending",
    chipApproved: "Approved",
    chipRejected: "Rejected",
    chipProposed: "Proposed",
    
    btnApprove: "Approve",
    btnReject: "Reject",
    btnPropose: "Propose",
    btnNote: "Add Note",
    btnSettings: "Settings",
    
    notifyFetchFail: "Could not fetch appointments",
    notifyApproveOk: "Appointment approved",
    notifyRejectOk: "Appointment rejected",
    proposeOk: "Proposal sent",
    proposeFail: "Could not propose",
    noteSaved: "Note saved",
    noteSaveFail: "Could not save note",
    settingsSaved: "Settings saved",
    settingsSaveFail: "Could not save settings",
    
    settingsTitle: "Appointment Settings",
    settingsDesc: "Set your working hours and special days.",
    generalSettings: "General Settings",
    workingHours: "Working Hours",
    annualCalendar: "Annual Calendar",
    slotDuration: "Slot Duration",
    gapMinutes: "Gap Between",
    weeklyHours: "Weekly Hours",
    lunchBreak: "Lunch Break",
    open: "Open",
    closed: "Closed",
    start: "Start",
    end: "End",
    minutes: "min",
    addSpecialDay: "Add Special Day",
    date: "Date",
    description: "Description",
    exceptionType: "Type",
    exceptionTypes: {
      working: "Working Day",
      closed: "Closed Day",
    },
    
    dailyBookingTitle: "Add Daily Appointment",
    dailyBookingDesc: "Add custom appointment for a specific date.",
    selectDate: "Select Date",
    addTimeSlot: "Add Time Slot",
    customerName: "Customer Name",
    customerPhone: "Phone",
    customerEmail: "Email",
    note: "Note",
    saveDailyBooking: "Save Appointment",
    
    proposeTitle: "Propose New Time",
    proposeInfo: "Propose a new appointment time.",
    proposeDate: "Date",
    proposeStart: "Start",
    proposeEnd: "End",
    proposeMessage: "Message",
    
    noteTitle: "Organization Note",
    notePlaceholder: "Write your internal note...",
    
    warnDisabled: "Booking system is disabled. Enable in Settings.",
  },
  de: {
    pageTitle: "Terminverwaltung",
    pageDesc: "Verwalten Sie alle Termine an einem Ort.",
    refresh: "Aktualisieren",
    loading: "Laden...",
    empty: "Keine Termine gefunden.",
    save: "Speichern",
    saving: "Speichern...",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    add: "Hinzufügen",
    
    statsTotal: "Gesamt",
    statsPending: "Ausstehend",
    statsApproved: "Bestätigt",
    statsRejected: "Abgelehnt",
    statsToday: "Heute",
    
    searchPlaceholder: "Suche: Name, Telefon, Datum...",
    statusLabel: "Status",
    statusAll: "Alle",
    statusPending: "Ausstehend",
    statusApproved: "Bestätigt",
    statusRejected: "Abgelehnt",
    statusProposed: "Vorgeschlagen",
    startDate: "Start",
    endDate: "Ende",
    clearFilters: "Löschen",
    
    chipPending: "Ausstehend",
    chipApproved: "Bestätigt",
    chipRejected: "Abgelehnt",
    chipProposed: "Vorgeschlagen",
    
    btnApprove: "Bestätigen",
    btnReject: "Ablehnen",
    btnPropose: "Vorschlagen",
    btnNote: "Notiz",
    btnSettings: "Einstellungen",
    
    notifyFetchFail: "Termine konnten nicht geladen werden",
    notifyApproveOk: "Termin bestätigt",
    notifyRejectOk: "Termin abgelehnt",
    proposeOk: "Vorschlag gesendet",
    proposeFail: "Vorschlag fehlgeschlagen",
    noteSaved: "Notiz gespeichert",
    noteSaveFail: "Notiz konnte nicht gespeichert werden",
    settingsSaved: "Einstellungen gespeichert",
    settingsSaveFail: "Einstellungen konnten nicht gespeichert werden",
    
    settingsTitle: "Termineinstellungen",
    settingsDesc: "Konfigurieren Sie Arbeitszeiten und besondere Tage.",
    generalSettings: "Allgemein",
    workingHours: "Arbeitszeiten",
    annualCalendar: "Jahreskalender",
    slotDuration: "Termindauer",
    gapMinutes: "Pause",
    weeklyHours: "Wöchentliche Zeiten",
    lunchBreak: "Mittagspause",
    open: "Geöffnet",
    closed: "Geschlossen",
    start: "Start",
    end: "Ende",
    minutes: "Min.",
    addSpecialDay: "Besonderen Tag hinzufügen",
    date: "Datum",
    description: "Beschreibung",
    exceptionType: "Typ",
    exceptionTypes: {
      working: "Arbeitstag",
      closed: "Geschlossen",
    },
    
    dailyBookingTitle: "Täglichen Termin hinzufügen",
    dailyBookingDesc: "Fügen Sie einen benutzerdefinierten Termin für ein bestimmtes Datum hinzu.",
    selectDate: "Datum auswählen",
    addTimeSlot: "Zeitslot hinzufügen",
    customerName: "Kundenname",
    customerPhone: "Telefon",
    customerEmail: "E-Mail",
    note: "Notiz",
    saveDailyBooking: "Termin speichern",
    
    proposeTitle: "Neue Zeit vorschlagen",
    proposeInfo: "Schlagen Sie eine neue Terminzeit vor.",
    proposeDate: "Datum",
    proposeStart: "Start",
    proposeEnd: "Ende",
    proposeMessage: "Nachricht",
    
    noteTitle: "Organisationsnotiz",
    notePlaceholder: "Notiz schreiben...",
    
    warnDisabled: "Terminsystem deaktiviert. In Einstellungen aktivieren.",
  },
};

/* ==================== STATUS META ==================== */
function statusMeta(raw, T) {
  const d = String(raw || "").toLowerCase();
  const map = {
    "pending": { label: T.chipPending, color: "warning", icon: <PendingIcon /> },
    "approved": { label: T.chipApproved, color: "success", icon: <CheckCircleIcon /> },
    "rejected": { label: T.chipRejected, color: "error", icon: <CancelIcon /> },
    "proposed": { label: T.chipProposed, color: "info", icon: <ScheduleSendIcon /> },
  };
  return map[d] || { label: raw, color: "default", icon: null };
}

/* ==================== STAT CARD ==================== */
function StatCard({ label, value, icon, color, trend }) {
  return (
    <StyledCard>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            {trend && (
              <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <TrendingUpIcon fontSize="inherit" />
                {trend}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color: color, width: 56, height: 56 }}>
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </StyledCard>
  );
}

/* ==================== ANA BİLEŞEN ==================== */
export default function BookingPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  
  const uiLang = useMemo(() => getUiLang(), []);
  const T = I18N[uiLang] || I18N.tr;
  
  // State'ler
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bookings, setBookings] = useState([]);
  
  // Filtreler
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  
  // Ayarlar
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [slotDuration, setSlotDuration] = useState(30);
  const [gapMinutes, setGapMinutes] = useState(0);
  const [bookingEnabled, setBookingEnabled] = useState(false);
  const [hours, setHours] = useState(() => {
    const base = {};
    DAYS.forEach(d => { 
      base[d.key] = { 
        enabled: d.key !== "sunday", 
        start: "09:00", 
        end: "18:00",
        lunchEnabled: true,
        lunchStart: "12:00",
        lunchEnd: "13:00"
      }; 
    });
    base.saturday = { enabled: true, start: "10:00", end: "14:00", lunchEnabled: false };
    base.sunday = { enabled: false, start: "09:00", end: "18:00", lunchEnabled: true };
    return base;
  });
  
  // Özel günler
  const [exceptions, setExceptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Günlük randevu ekleme state'leri
  const [dailyBookingOpen, setDailyBookingOpen] = useState(false);
  const [dailyTimeSlots, setDailyTimeSlots] = useState([]);
  const [dailyCustomer, setDailyCustomer] = useState({
    name: '',
    phone: '',
    email: '',
    note: ''
  });
  
  // Dialog'lar
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeBooking, setProposeBooking] = useState(null);
  const [proposeData, setProposeData] = useState({ date: "", start: "10:00", end: "10:30", message: "" });
  
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBooking, setNoteBooking] = useState(null);
  const [noteText, setNoteText] = useState("");
  
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [editingException, setEditingException] = useState(null);

  // İstatistikler
  const stats = useMemo(() => {
    const list = Array.isArray(bookings) ? bookings : [];
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: list.length,
      pending: list.filter(b => b.durum === "pending").length,
      approved: list.filter(b => b.durum === "approved").length,
      rejected: list.filter(b => b.durum === "rejected").length,
      today: list.filter(b => b.tarih === today).length,
    };
  }, [bookings]);

  // Randevuları grupla
  const groupedBookings = useMemo(() => {
    const groups = {};
    (Array.isArray(bookings) ? bookings : []).forEach(booking => {
      if (!groups[booking.tarih]) groups[booking.tarih] = [];
      groups[booking.tarih].push(booking);
    });
    return groups;
  }, [bookings]);

  // Veri çekme
  const fetchBookings = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const res = await fetch(ENDPOINTS.bookings, { headers: authHeaders() });
      const json = await res.json();
      setBookings(normalizeList(json));
    } catch (error) {
      notify(T.notifyFetchFail, { type: "error" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const res = await fetch(ENDPOINTS.settingsGet, { headers: authHeaders() });
      const json = await res.json();
      const data = json?.data || {};
      setSlotDuration(data.slot_suresi || 30);
      setGapMinutes(data.gap_minutes || 0);
      setBookingEnabled(data.randevu_aktif || false);
      if (data.hours) setHours(prev => ({ ...prev, ...data.hours }));
      setExceptions(Array.isArray(data.exceptions) ? data.exceptions : []);
    } catch (error) {
      notify("Ayarlar yüklenemedi", { type: "error" });
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); fetchSettings(); }, []);

  // Randevu durumu güncelle
  const updateStatus = async (id, action) => {
    try {
      const url = action === "approve" ? ENDPOINTS.approve(id) : ENDPOINTS.reject(id);
      const res = await fetch(url, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error(T.notifyActionFail);
      notify(action === "approve" ? T.notifyApproveOk : T.notifyRejectOk, { type: "success" });
      await fetchBookings(true);
    } catch (error) {
      notify(error.message, { type: "error" });
    }
  };

  // Ayarları kaydet
  const saveSettings = async () => {
    try {
      setSettingsSaving(true);
      const payload = { 
        slot_suresi: slotDuration, 
        gap_minutes: gapMinutes, 
        randevu_aktif: bookingEnabled, 
        hours, 
        exceptions 
      };
      const res = await fetch(ENDPOINTS.settingsSave, {
        method: "PUT", headers: authHeaders(), body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(T.settingsSaveFail);
      notify(T.settingsSaved, { type: "success" });
      await fetchSettings();
    } catch (error) {
      notify(error.message, { type: "error" });
    } finally {
      setSettingsSaving(false);
    }
  };

  // Günlük randevu ekleme fonksiyonları
  const addTimeSlot = () => {
    setDailyTimeSlots([
      ...dailyTimeSlots,
      { start: "10:00", end: "10:30", id: Date.now() }
    ]);
  };

  const updateTimeSlot = (id, field, value) => {
    setDailyTimeSlots(dailyTimeSlots.map(slot => 
      slot.id === id ? { ...slot, [field]: value } : slot
    ));
  };

  const removeTimeSlot = (id) => {
    setDailyTimeSlots(dailyTimeSlots.filter(slot => slot.id !== id));
  };

  const saveDailyBooking = async () => {
  if (!selectedDate || dailyTimeSlots.length === 0) {
    notify("Lütfen tarih ve en az bir saat aralığı seçin", { type: "warning" });
    return;
  }

  try {
    setSettingsSaving(true);

    for (const slot of dailyTimeSlots) {
      const payload = {
        tarih: selectedDate.toISOString().slice(0, 10),
        baslangic: slot.start,
        bitis: slot.end,
        musteri_ad: dailyCustomer.name || "Özel Randevu",
        musteri_telefon: dailyCustomer.phone || "",
        musteri_email: dailyCustomer.email || "",
        kullanici_notu: dailyCustomer.note || ""
      };

      console.log("📤 Gönderilen payload:", payload);

      const res = await fetch(ENDPOINTS.bookings + '/custom', {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Randevu eklenemedi");
      }
      
      const data = await res.json();
      console.log("📥 Cevap:", data);
    }

    notify("Randevular başarıyla eklendi", { type: "success" });
    setDailyBookingOpen(false);
    setDailyTimeSlots([]);
    setDailyCustomer({ name: '', phone: '', email: '', note: '' });
    await fetchBookings(true);
  } catch (error) {
    console.error("❌ Hata:", error);
    notify("Randevu eklenirken hata oluştu: " + error.message, { type: "error" });
  } finally {
    setSettingsSaving(false);
  }
};


  // Saat öner
  const sendPropose = async () => {
    if (!proposeBooking?.id || !proposeData.date || !proposeData.start || !proposeData.end) {
      notify("Tarih ve saat seçin", { type: "warning" });
      return;
    }
    try {
      const res = await fetch(ENDPOINTS.propose(proposeBooking.id), {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ 
          tarih: proposeData.date, 
          baslangic: proposeData.start, 
          bitis: proposeData.end, 
          mesaj: proposeData.message 
        })
      });
      if (!res.ok) throw new Error(T.proposeFail);
      notify(T.proposeOk, { type: "success" });
      setProposeOpen(false);
      setProposeBooking(null);
      await fetchBookings(true);
    } catch (error) {
      notify(error.message, { type: "error" });
    }
  };

  // Not kaydet
  const saveNote = async () => {
    if (!noteBooking?.id) return;
    try {
      const res = await fetch(ENDPOINTS.note(noteBooking.id), {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ kurum_notu: noteText })
      });
      if (!res.ok) throw new Error(T.noteSaveFail);
      notify(T.noteSaved, { type: "success" });
      setNoteOpen(false);
      setNoteBooking(null);
      setNoteText("");
      await fetchBookings(true);
    } catch (error) {
      notify(error.message, { type: "error" });
    }
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setDateRange({ start: "", end: "" });
  };

  // Özel gün ekle
  const handleAddException = (exception) => {
    if (exceptions.find(e => e.date === exception.date)) {
      notify("Bu tarih zaten eklenmiş!", { type: "warning" });
      return;
    }
    setExceptions([...exceptions, exception]);
    setExceptionDialogOpen(false);
    setEditingException(null);
  };

  // Özel gün sil
  const handleDeleteException = (date) => {
    setExceptions(exceptions.filter(e => e.date !== date));
  };

  // Gün adını getir
  const getDayName = (key, lang = uiLang) => {
    const day = DAYS.find(d => d.key === key);
    return day ? day[lang] || day.tr : key;
  };

  // Güvenli exceptions listesi
  const safeExceptions = useMemo(() => {
    return Array.isArray(exceptions) ? exceptions : [];
  }, [exceptions]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={getDateFnsLocale(uiLang)}>
      <Box sx={{ width: "100%", px: { xs: 1, sm: 2, md: 3 }, pb: 6 }}>
        
        {/* HEADER */}
        <GradientHeader elevation={0}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
            <Box>
              <Typography variant="h4" fontWeight={900} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EventAvailableIcon fontSize="large" /> 
                {T.pageTitle}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                {T.pageDesc}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1}>
              <Tooltip title={T.refresh}>
                <IconButton 
                  onClick={() => fetchBookings()} 
                  sx={{ 
                    color: "white", 
                    bgcolor: "rgba(255,255,255,0.1)",
                    '&:hover': { bgcolor: "rgba(255,255,255,0.2)" }
                  }}
                >
                  {refreshing ? <CircularProgress size={24} color="inherit" /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
              
              <Button
                variant="contained"
                startIcon={<RestaurantIcon />}
                onClick={() => navigate("/restoran-masalar")}
                sx={{
                  bgcolor: "white",
                  color: COLORS.secondary,
                  fontWeight: 700,
                  '&:hover': { bgcolor: "rgba(255,255,255,0.9)" }
                }}
              >
                Restoran Masaları
              </Button>
            </Stack>
          </Stack>
        </GradientHeader>

        {/* UYARI */}
        <Zoom in={!bookingEnabled}>
          <Alert 
            severity="warning" 
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setTab(1)}>
                {T.btnSettings}
              </Button>
            }
          >
            {T.warnDisabled}
          </Alert>
        </Zoom>

        {/* STATS CARDS */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: 2,
            mb: 3,
          }}
        >
          <StatCard label={T.statsTotal} value={stats.total} icon={<EventAvailableIcon />} color={COLORS.primary} />
          <StatCard label={T.statsPending} value={stats.pending} icon={<PendingIcon />} color={COLORS.warning} />
          <StatCard label={T.statsApproved} value={stats.approved} icon={<CheckCircleIcon />} color={COLORS.success} />
          <StatCard label={T.statsRejected} value={stats.rejected} icon={<CancelIcon />} color={COLORS.error} />
          <StatCard label={T.statsToday} value={stats.today} icon={<CalendarTodayIcon />} color={COLORS.secondary} />
        </Box>

        {/* TABS */}
        <StyledCard variant="outlined">
          <Tabs 
            value={tab} 
            onChange={(e, v) => setTab(v)} 
            sx={{ 
              borderBottom: 1, 
              borderColor: "divider",
              px: 2,
              pt: 1
            }}
            variant={isMobile ? "fullWidth" : "standard"}
          >
            <Tab 
              label={T.statsTotal} 
              icon={<EventAvailableIcon />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab 
              label={T.btnSettings} 
              icon={<SettingsIcon />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>

          {/* TAB 0: RANDEVULAR */}
          {tab === 0 && (
            <CardContent>
              {/* Filtreler */}
              <Fade in={true}>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={T.searchPlaceholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <TextField
                      select
                      size="small"
                      label={T.statusLabel}
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="all">{T.statusAll}</MenuItem>
                      <MenuItem value="pending">{T.statusPending}</MenuItem>
                      <MenuItem value="approved">{T.statusApproved}</MenuItem>
                      <MenuItem value="rejected">{T.statusRejected}</MenuItem>
                      <MenuItem value="proposed">{T.statusProposed}</MenuItem>
                    </TextField>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                    <TextField
                      type="date"
                      size="small"
                      label={T.startDate}
                      value={dateRange.start}
                      onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth={isMobile}
                    />
                    <TextField
                      type="date"
                      size="small"
                      label={T.endDate}
                      value={dateRange.end}
                      onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth={isMobile}
                    />
                    
                    <Button 
                      variant="text" 
                      onClick={clearFilters} 
                      sx={{ color: COLORS.secondary, whiteSpace: 'nowrap' }}
                    >
                      {T.clearFilters}
                    </Button>
                  </Stack>
                </Stack>
              </Fade>

              {/* Randevu Listesi */}
              {loading ? (
                <Box sx={{ py: 8, textAlign: "center" }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }} color="text.secondary">
                    {T.loading}
                  </Typography>
                </Box>
              ) : Object.keys(groupedBookings).length === 0 ? (
                <Paper sx={{ p: 4, textAlign: "center", bgcolor: 'grey.50' }}>
                  <EventBusyIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                  <Typography color="text.secondary">{T.empty}</Typography>
                </Paper>
              ) : (
                <Stack spacing={4}>
                  {Object.keys(groupedBookings).sort().reverse().map((date, index) => (
                    <Grow in={true} timeout={300 * (index + 1)} key={date}>
                      <Box>
                        <Typography 
                          variant="h6" 
                          fontWeight={700} 
                          sx={{ 
                            mb: 2, 
                            color: COLORS.primary,
                            borderBottom: `2px solid ${alpha(COLORS.primary, 0.2)}`,
                            pb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          {new Date(date).toLocaleDateString(
                            uiLang === 'tr' ? 'tr-TR' : uiLang === 'de' ? 'de-DE' : 'en-US',
                            { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                          )}
                          <Chip 
                            size="small" 
                            label={`${groupedBookings[date].length} randevu`} 
                            sx={{ bgcolor: alpha(COLORS.primary, 0.1), color: COLORS.primary, fontWeight: 600 }}
                          />
                        </Typography>
                        
                        <Stack spacing={2}>
                          {groupedBookings[date].map((booking) => {
                            const meta = statusMeta(booking.durum, T);
                            return (
                              <Slide direction="up" in={true} mountOnEnter unmountOnExit key={booking.id}>
                                <Paper 
                                  variant="outlined" 
                                  sx={{ 
                                    p: 2, 
                                    borderRadius: 2,
                                    borderLeft: `4px solid ${meta.color === 'warning' ? COLORS.warning : 
                                                                meta.color === 'success' ? COLORS.success :
                                                                meta.color === 'error' ? COLORS.error : COLORS.info}`,
                                    '&:hover': {
                                      boxShadow: 2,
                                      bgcolor: alpha(meta.color === 'warning' ? COLORS.warning :
                                                      meta.color === 'success' ? COLORS.success :
                                                      meta.color === 'error' ? COLORS.error : COLORS.info, 0.02)
                                    }
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                      <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                          <AccessTimeIcon fontSize="small" color="action" />
                                          <Typography fontWeight={600}>
                                            {booking.baslangic} - {booking.bitis}
                                          </Typography>
                                          <StatusChip
                                            size="small"
                                            icon={meta.icon}
                                            label={meta.label}
                                            statuscolor={meta.color}
                                          />
                                        </Stack>

                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                          {booking.ad_soyad && (
                                            <Chip
                                              size="small"
                                              icon={<PersonIcon />}
                                              label={booking.ad_soyad}
                                              variant="outlined"
                                            />
                                          )}
                                          {booking.telefon && (
                                            <Chip
                                              size="small"
                                              icon={<PhoneIcon />}
                                              label={booking.telefon}
                                              variant="outlined"
                                            />
                                          )}
                                          {booking.email && (
                                            <Chip
                                              size="small"
                                              icon={<EmailIcon />}
                                              label={booking.email}
                                              variant="outlined"
                                            />
                                          )}
                                        </Stack>

                                        {booking.kullanici_notu && (
                                          <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                              <NotesIcon fontSize="inherit" />
                                              {T.userNote}:
                                            </Typography>
                                            <Typography variant="body2">{booking.kullanici_notu}</Typography>
                                          </Box>
                                        )}

                                        {booking.kurum_notu && (
                                          <Box sx={{ bgcolor: alpha(COLORS.info, 0.05), p: 1.5, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                              <NotesIcon fontSize="inherit" />
                                              {T.orgNote}:
                                            </Typography>
                                            <Typography variant="body2">{booking.kurum_notu}</Typography>
                                          </Box>
                                        )}
                                      </Stack>
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                      <Stack 
                                        direction={{ xs: 'row', md: 'column' }} 
                                        spacing={1} 
                                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                                        flexWrap="wrap"
                                        sx={{ height: '100%' }}
                                      >
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          startIcon={<NotesIcon />}
                                          onClick={() => {
                                            setNoteBooking(booking);
                                            setNoteText(booking.kurum_notu || "");
                                            setNoteOpen(true);
                                          }}
                                          sx={{ minWidth: isMobile ? 'auto' : 100 }}
                                        >
                                          {T.btnNote}
                                        </Button>

                                        {booking.durum === "pending" && (
                                          <>
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="success"
                                              startIcon={<CheckCircleOutlineIcon />}
                                              onClick={() => updateStatus(booking.id, "approve")}
                                              sx={{ minWidth: isMobile ? 'auto' : 100 }}
                                            >
                                              {T.btnApprove}
                                            </Button>
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="error"
                                              startIcon={<HighlightOffIcon />}
                                              onClick={() => updateStatus(booking.id, "reject")}
                                              sx={{ minWidth: isMobile ? 'auto' : 100 }}
                                            >
                                              {T.btnReject}
                                            </Button>
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="info"
                                              startIcon={<ScheduleSendIcon />}
                                              onClick={() => {
                                                setProposeBooking(booking);
                                                setProposeData({
                                                  date: booking.tarih || "",
                                                  start: booking.baslangic || "10:00",
                                                  end: booking.bitis || "10:30",
                                                  message: "",
                                                });
                                                setProposeOpen(true);
                                              }}
                                              sx={{ minWidth: isMobile ? 'auto' : 100 }}
                                            >
                                              {T.btnPropose}
                                            </Button>
                                          </>
                                        )}
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              </Slide>
                            );
                          })}
                        </Stack>
                      </Box>
                    </Grow>
                  ))}
                </Stack>
              )}
            </CardContent>
          )}

          {/* TAB 1: AYARLAR */}
          {tab === 1 && (
            <CardContent>
              {settingsLoading ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>{T.loading}</Typography>
                </Box>
              ) : (
                <Stack spacing={3}>
                  <Typography variant="h6" fontWeight={900}>
                    {T.settingsTitle}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {T.settingsDesc}
                  </Typography>

                  <Divider />

                  {/* Genel Ayarlar */}
                  <StyledCard variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SettingsIcon color="primary" />
                      {T.generalSettings}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={bookingEnabled}
                          onChange={(e) => setBookingEnabled(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: COLORS.success,
                            },
                          }}
                        />
                      }
                      label={T.settingsTitle}
                      sx={{ mb: 2 }}
                    />

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label={T.slotDuration}
                          value={slotDuration}
                          onChange={(e) => setSlotDuration(Number(e.target.value))}
                        >
                          {SLOT_DURATIONS.map((dur) => (
                            <MenuItem key={dur} value={dur}>
                              {dur} {T.minutes}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={T.gapMinutes}
                          value={gapMinutes}
                          onChange={(e) => setGapMinutes(Number(e.target.value))}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{T.minutes}</InputAdornment>,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </StyledCard>

                  {/* Haftalık Çalışma Saatleri */}
                  <StyledCard variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WorkIcon color="primary" />
                      {T.weeklyHours}
                    </Typography>

                    <Stack spacing={2}>
                      {DAYS.map((day) => {
                        const h = hours[day.key] || { 
                          enabled: false, 
                          start: "09:00", 
                          end: "18:00",
                          lunchEnabled: false,
                          lunchStart: "12:00",
                          lunchEnd: "13:00"
                        };
                        
                        return (
                          <Paper 
                            key={day.key} 
                            variant="outlined" 
                            sx={{ 
                              p: 2,
                              bgcolor: h.enabled ? alpha(COLORS.success, 0.02) : alpha(COLORS.error, 0.02)
                            }}
                          >
                            <Stack spacing={2}>
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                <Typography sx={{ minWidth: 100, fontWeight: 600 }}>
                                  {getDayName(day.key)}
                                </Typography>
                                
                                <Chip
                                  label={h.enabled ? T.open : T.closed}
                                  color={h.enabled ? "success" : "default"}
                                  onClick={() => {
                                    setHours({
                                      ...hours,
                                      [day.key]: { ...h, enabled: !h.enabled },
                                    });
                                  }}
                                  sx={{ cursor: 'pointer' }}
                                />
                              </Stack>
                              
                              {h.enabled && (
                                <Fade in={true}>
                                  <Stack spacing={2}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                      <TextField
                                        type="time"
                                        size="small"
                                        label={T.start}
                                        value={h.start}
                                        onChange={(e) => {
                                          setHours({
                                            ...hours,
                                            [day.key]: { ...h, start: e.target.value },
                                          });
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                      <TextField
                                        type="time"
                                        size="small"
                                        label={T.end}
                                        value={h.end}
                                        onChange={(e) => {
                                          setHours({
                                            ...hours,
                                            [day.key]: { ...h, end: e.target.value },
                                          });
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Stack>

                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            size="small"
                                            checked={h.lunchEnabled || false}
                                            onChange={(e) => {
                                              setHours({
                                                ...hours,
                                                [day.key]: { ...h, lunchEnabled: e.target.checked },
                                              });
                                            }}
                                          />
                                        }
                                        label={
                                          <Stack direction="row" spacing={0.5} alignItems="center">
                                            <LunchDiningIcon fontSize="small" />
                                            <Typography variant="body2">{T.lunchBreak}</Typography>
                                          </Stack>
                                        }
                                      />
                                      
                                      {h.lunchEnabled && (
                                        <Grow in={true}>
                                          <Stack direction="row" spacing={2}>
                                            <TextField
                                              type="time"
                                              size="small"
                                              label={T.start}
                                              value={h.lunchStart || "12:00"}
                                              onChange={(e) => {
                                                setHours({
                                                  ...hours,
                                                  [day.key]: { ...h, lunchStart: e.target.value },
                                                });
                                              }}
                                              InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                              type="time"
                                              size="small"
                                              label={T.end}
                                              value={h.lunchEnd || "13:00"}
                                              onChange={(e) => {
                                                setHours({
                                                  ...hours,
                                                  [day.key]: { ...h, lunchEnd: e.target.value },
                                                });
                                              }}
                                              InputLabelProps={{ shrink: true }}
                                            />
                                          </Stack>
                                        </Grow>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Fade>
                              )}
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </StyledCard>

                  {/* GÜNLÜK RANDEVU EKLEME - YENİ BÖLÜM */}
                  <StyledCard variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon color="primary" />
                      {T.dailyBookingTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {T.dailyBookingDesc}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label={T.selectDate}
                          value={selectedDate}
                          onChange={(newValue) => setSelectedDate(newValue)}
                          renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setDailyBookingOpen(true)}
                          sx={{ height: '56px' }}
                        >
                          {T.addTimeSlot}
                        </Button>
                      </Grid>
                    </Grid>

                    {/* Seçili tarihteki özel randevular */}
                    {selectedDate && dailyTimeSlots.length > 0 && (
                      <Fade in={true}>
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {selectedDate.toLocaleDateString(uiLang === 'tr' ? 'tr-TR' : uiLang === 'de' ? 'de-DE' : 'en-US')} - Eklenen Randevular:
                          </Typography>
                          {dailyTimeSlots.map((slot) => (
                            <Paper key={slot.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={5} sm={3}>
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={slot.start}
                                    onChange={(e) => updateTimeSlot(slot.id, 'start', e.target.value)}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={5} sm={3}>
                                  <TextField
                                    size="small"
                                    type="time"
                                    value={slot.end}
                                    onChange={(e) => updateTimeSlot(slot.id, 'end', e.target.value)}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={2} sm={1}>
                                  <IconButton color="error" onClick={() => removeTimeSlot(slot.id)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Paper>
                          ))}
                        </Box>
                      </Fade>
                    )}
                  </StyledCard>

                  {/* Yıllık Takvim */}
                  <StyledCard variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DateRangeIcon color="primary" />
                      {T.annualCalendar}
                    </Typography>
                    
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => { setEditingException(null); setExceptionDialogOpen(true); }}
                      fullWidth={isMobile}
                      sx={{ mb: 2 }}
                    >
                      {T.addSpecialDay}
                    </Button>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>{T.date}</TableCell>
                            <TableCell>{T.exceptionType}</TableCell>
                            <TableCell>{T.description}</TableCell>
                            <TableCell>{T.workingHours}</TableCell>
                            <TableCell align="right">{T.delete}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {safeExceptions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                <Typography color="text.secondary">
                                  {T.addSpecialDay}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            safeExceptions
                              .sort((a, b) => a.date.localeCompare(b.date))
                              .map((ex) => (
                                <TableRow key={ex.date} hover>
                                  <TableCell>{ex.date}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      size="small" 
                                      label={ex.type === 'working' ? T.exceptionTypes.working : T.exceptionTypes.closed} 
                                      color={ex.type === 'working' ? 'success' : 'error'}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>{ex.description || '-'}</TableCell>
                                  <TableCell>
                                    {ex.start && ex.end ? `${ex.start} - ${ex.end}` : '-'}
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton 
                                      size="small" 
                                      color="error" 
                                      onClick={() => handleDeleteException(ex.date)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </StyledCard>

                  {/* Kaydet Butonu */}
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                    <Button variant="outlined" onClick={fetchSettings}>
                      {T.refresh}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={saveSettings}
                      disabled={settingsSaving}
                      startIcon={settingsSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {settingsSaving ? T.saving : T.save}
                    </Button>
                  </Box>
                </Stack>
              )}
            </CardContent>
          )}
        </StyledCard>

        {/* GÜNLÜK RANDEVU EKLEME DIALOG */}
        <Dialog 
          open={dailyBookingOpen} 
          onClose={() => setDailyBookingOpen(false)} 
          maxWidth="md" 
          fullWidth
          TransitionComponent={Slide}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarTodayIcon color="primary" />
              <Typography variant="h6">
                {selectedDate?.toLocaleDateString(uiLang === 'tr' ? 'tr-TR' : uiLang === 'de' ? 'de-DE' : 'en-US')} - {T.dailyBookingTitle}
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3}>
              <Alert severity="info">
                {T.dailyBookingDesc}
              </Alert>

              {/* Müşteri Bilgileri */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Müşteri Bilgileri
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={T.customerName}
                      value={dailyCustomer.name}
                      onChange={(e) => setDailyCustomer({ ...dailyCustomer, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={T.customerPhone}
                      value={dailyCustomer.phone}
                      onChange={(e) => setDailyCustomer({ ...dailyCustomer, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={T.customerEmail}
                      value={dailyCustomer.email}
                      onChange={(e) => setDailyCustomer({ ...dailyCustomer, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label={T.note}
                      value={dailyCustomer.note}
                      onChange={(e) => setDailyCustomer({ ...dailyCustomer, note: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Zaman Aralıkları */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {T.addTimeSlot}
                </Typography>
                
                {dailyTimeSlots.map((slot, index) => (
                  <Paper key={slot.id} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography variant="body2" sx={{ minWidth: 30 }}>{index + 1}.</Typography>
                        <TextField
                          size="small"
                          type="time"
                          label={T.proposeStart}
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(slot.id, 'start', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          size="small"
                          type="time"
                          label={T.proposeEnd}
                          value={slot.end}
                          onChange={(e) => updateTimeSlot(slot.id, 'end', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                        <IconButton color="error" onClick={() => removeTimeSlot(slot.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={addTimeSlot}
                  variant="outlined"
                  fullWidth
                >
                  {T.addTimeSlot}
                </Button>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDailyBookingOpen(false)}>{T.cancel}</Button>
            <Button 
              onClick={saveDailyBooking} 
              variant="contained" 
              color="primary"
              startIcon={<SaveIcon />}
            >
              {T.saveDailyBooking}
            </Button>
          </DialogActions>
        </Dialog>

        {/* PROPOSE DIALOG */}
        <Dialog 
          open={proposeOpen} 
          onClose={() => setProposeOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Slide}
          transitionDuration={300}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ScheduleSendIcon color="info" />
              <Typography variant="h6">{T.proposeTitle}</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Alert severity="info" icon={<ScheduleSendIcon />}>
                {T.proposeInfo}
              </Alert>
              
              <TextField
                type="date"
                label={T.proposeDate}
                value={proposeData.date}
                onChange={(e) => setProposeData({ ...proposeData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  type="time"
                  label={T.proposeStart}
                  value={proposeData.start}
                  onChange={(e) => setProposeData({ ...proposeData, start: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  type="time"
                  label={T.proposeEnd}
                  value={proposeData.end}
                  onChange={(e) => setProposeData({ ...proposeData, end: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
              
              <TextField
                multiline
                rows={3}
                label={T.proposeMessage}
                placeholder={T.proposeMessagePlaceholder}
                value={proposeData.message}
                onChange={(e) => setProposeData({ ...proposeData, message: e.target.value })}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setProposeOpen(false)}>{T.cancel}</Button>
            <Button 
              onClick={sendPropose} 
              variant="contained" 
              color="info"
              startIcon={<ScheduleSendIcon />}
            >
              {T.send}
            </Button>
          </DialogActions>
        </Dialog>

        {/* NOTE DIALOG */}
        <Dialog 
          open={noteOpen} 
          onClose={() => setNoteOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Slide}
          transitionDuration={300}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <NotesIcon color="primary" />
              <Typography variant="h6">{T.noteTitle}</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <TextField
              multiline
              rows={4}
              placeholder={T.notePlaceholder}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setNoteOpen(false)}>{T.cancel}</Button>
            <Button 
              onClick={saveNote} 
              variant="contained"
              startIcon={<SaveIcon />}
            >
              {T.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* EXCEPTION DIALOG */}
        <Dialog 
          open={exceptionDialogOpen} 
          onClose={() => setExceptionDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          TransitionComponent={Slide}
          transitionDuration={300}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <DateRangeIcon color="primary" />
              <Typography variant="h6">
                {editingException ? T.edit : T.addSpecialDay}
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <ExceptionDayForm 
              initialData={editingException} 
              onSave={handleAddException} 
              onCancel={() => setExceptionDialogOpen(false)} 
              T={T} 
            />
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

/* ==================== İSTİSNA GÜN FORM BİLEŞENİ ==================== */
function ExceptionDayForm({ initialData, onSave, onCancel, T }) {
  const [formData, setFormData] = useState(
    initialData || { 
      date: '', 
      type: 'closed', 
      start: '09:00', 
      end: '18:00', 
      description: '' 
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanData = { 
      date: formData.date, 
      type: formData.type, 
      description: formData.description || '' 
    };
    if (formData.type === 'working') { 
      cleanData.start = formData.start; 
      cleanData.end = formData.end; 
    }
    onSave(cleanData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <TextField
          type="date"
          label={T.date}
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
          required
        />

        <FormControl fullWidth>
          <InputLabel>{T.exceptionType}</InputLabel>
          <Select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            label={T.exceptionType}
          >
            <MenuItem value="closed">
              <Stack direction="row" spacing={1} alignItems="center">
                <CancelIcon fontSize="small" color="error" />
                <span>{T.exceptionTypes.closed}</span>
              </Stack>
            </MenuItem>
            <MenuItem value="working">
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon fontSize="small" color="success" />
                <span>{T.exceptionTypes.working}</span>
              </Stack>
            </MenuItem>
          </Select>
        </FormControl>

        {formData.type === 'working' && (
          <Grow in={true}>
            <Stack direction="row" spacing={2}>
              <TextField
                type="time"
                label={T.start}
                value={formData.start}
                onChange={(e) => setFormData({ ...formData, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="time"
                label={T.end}
                value={formData.end}
                onChange={(e) => setFormData({ ...formData, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </Grow>
        )}

        <TextField
          label={T.description}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          multiline
          rows={2}
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onCancel}>{T.cancel}</Button>
          <Button type="submit" variant="contained">
            {T.save}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}